import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { faChevronLeft } from "@fortawesome/pro-solid-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { getUrls } from "@portal/app/_common/lib/urls";
import { paletteOptions } from "@portal/app/_components/theme/options/palette";
import NextLink from "next/link";
import portraitMarco from "@portal/app/_components/resources/images/homepage_portrait_marco.webp";
import portraitLaura from "@portal/app/_components/resources/images/homepage_portrait_laura.webp";
import portraitVictoria from "@portal/app/_components/resources/images/homepage_portrait_victoria.webp";
import portraitJaylen from "@portal/app/_components/resources/images/homepage_portrait_jaylen.webp";
import portraitAaliyah from "@portal/app/_components/resources/images/homepage_portrait_aaliyah.webp";

const testimonials = [
  {
    name: "Marco T.",
    program: "IT Technician",
    text: "I'm 51 and was worried it was too late to go back to school. But Dreambound gave me the confidence to start.",
    image: portraitMarco,
  },
  {
    name: "Laura G.",
    program: "Healthcare Administration",
    text: "Dreambound saved me from researching uselessly for hours.",
    image: portraitLaura,
  },
  {
    name: "Victoria M.",
    program: "Medical Assistant",
    text: "All the schools I previously looked into, you had to pay out of pocket. Dreambound found me a school where I could get financial aid.",
    image: portraitVictoria,
  },
  {
    name: "Jaylen W.",
    program: "Phlebotomy",
    text: "Dreambound stood strong with me from finding a class to enrollment. Wonderful people with a strong mission.",
    image: portraitJaylen,
  },
  {
    name: "Aaliyah W.",
    program: "Digital Marketing",
    text: "If you aren't sure how to advance your career, Dreambound is THE place. It helps you figure out the right steps to better yourself.",
    image: portraitAaliyah,
  },
];

export const HomeTestimonialCarouselSection = () => {
  const urls = getUrls();

  return (
    <Section>
      <CarouselContainer role="region" aria-label="Student testimonials">
        <Swiper
          modules={[Navigation, Pagination]}
          navigation={{
            prevEl: ".testimonial_prev",
            nextEl: ".testimonial_next",
          }}
          pagination={{
            clickable: true,
            el: ".testimonial_pagination",
          }}
          slidesPerView={1}
          spaceBetween={0}
          loop
        >
          {testimonials.map((t) => (
            <SwiperSlide key={t.text}>
              <SlideLayout>
                <QuoteSide>
                  <QuoteContent>
                    <blockquote className="quote_text">
                      &ldquo;{t.text}&rdquo;
                    </blockquote>
                    <div className="quote_attribution">
                      <Typography className="quote_name">{t.name}</Typography>
                      {t.program && (
                        <Typography className="quote_program">
                          {t.program}
                        </Typography>
                      )}
                    </div>
                  </QuoteContent>
                </QuoteSide>
                <PortraitSide>
                  <div className="motion_blur_accent" />
                  <img
                    alt={`${t.name} testimonial`}
                    src={t.image.src}
                    loading="lazy"
                    className="portrait_image"
                  />
                </PortraitSide>
              </SlideLayout>
            </SwiperSlide>
          ))}
        </Swiper>

        <NavRow>
          <CarouselNavButtons>
            <CarouselNavButton
              className="testimonial_prev"
              aria-label="Previous testimonial"
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </CarouselNavButton>
            <CarouselNavButton
              className="testimonial_next"
              aria-label="Next testimonial"
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </CarouselNavButton>
          </CarouselNavButtons>
          <PaginationDots className="testimonial_pagination" />
          <NextLink href={urls.DreamWall()} className="cta_link">
            View our Dream Wall &rarr;
          </NextLink>
        </NavRow>
      </CarouselContainer>
    </Section>
  );
};

const Section = styled("section")`
  padding: 5rem 2rem;
  max-width: 60rem;
  width: 100%;
  margin: 0 auto;

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 3rem 1rem;
  }
`;

const CarouselContainer = styled("div")`
  width: 100%;
  overflow: hidden;
  border-radius: 1.5rem;
  background: white;
  border: 1px solid ${paletteOptions.dream.borderColor};

  .swiper {
    overflow: hidden;
    touch-action: pan-y;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    border-radius: 1rem;
  }
`;

const SlideLayout = styled(Box)`
  display: flex;
  position: relative;
  min-height: 28rem;

  ${({ theme }) => theme.breakpoints.down("md")} {
    flex-direction: column-reverse;
    min-height: auto;
  }
`;

const QuoteSide = styled(Box)`
  width: 60%;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4rem 3.5rem;

  ${({ theme }) => theme.breakpoints.down("lg")} {
    padding: 3rem 2.5rem;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
    padding: 2rem 1.5rem 2.5rem;
  }
`;

const QuoteContent = styled(Box)`
  max-width: 28rem;

  .quote_text {
    margin: 0;
    padding: 0;
    font-size: 1.5rem;
    line-height: 1.5;
    color: ${paletteOptions.text.primary};
    font-weight: 400;
    letter-spacing: -0.01em;
  }

  .quote_attribution {
    margin-top: 2rem;
    padding-top: 1.5rem;
    border-top: 2px solid ${paletteOptions.primary.main};
    display: inline-block;
  }

  .quote_name {
    font-size: 1.1rem;
    font-weight: 700;
    color: ${paletteOptions.text.primary};
  }

  .quote_program {
    font-size: 1rem;
    color: ${paletteOptions.primary.main};
    margin-top: 0.15rem;
    font-weight: 500;
  }

  ${({ theme }) => theme.breakpoints.down("lg")} {
    .quote_text {
      font-size: 1.25rem;
    }
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    .quote_text {
      font-size: 1.1rem;
    }

    .quote_attribution {
      margin-top: 1.5rem;
      padding-top: 1rem;
    }
  }
`;

const PortraitSide = styled(Box)`
  width: 40%;
  min-width: 18rem;
  position: absolute;
  right: 0;
  top: 0;
  bottom: 0;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
  justify-content: center;

  /* Motion blur accent behind the portrait */
  .motion_blur_accent {
    position: absolute;
    inset: 0;
    background: #d8e8f8 url("/motion-blur.webp") center center / cover;
  }

  .portrait_image {
    position: relative;
    z-index: 1;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: top center;
    display: block;
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    position: relative;
    width: 100%;
    min-width: unset;
    height: 20rem;
    border-radius: 0;
    pointer-events: none;
  }

  ${({ theme }) => theme.breakpoints.down("sm")} {
    height: 16rem;
  }
`;

const NavRow = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1rem 2rem 1.25rem;
  border-top: 1px solid ${paletteOptions.dream.borderColor};

  .cta_link {
    color: ${paletteOptions.primary.main};
    font-weight: 600;
    font-size: 1rem;
    text-decoration: underline;
    text-underline-offset: 0.3em;
    text-decoration-thickness: 1.5px;
    transition: opacity 0.2s ease;
    white-space: nowrap;

    &:hover {
      opacity: 0.7;
    }
  }

  ${({ theme }) => theme.breakpoints.down("md")} {
    padding: 1rem 1.25rem;
    display: grid;
    grid-template-columns: 1fr;
    justify-items: center;
    gap: 0.75rem;
  }
`;

const CarouselNavButtons = styled(Box)`
  display: flex;
  gap: 0.5rem;
`;

const CarouselNavButton = styled("button")`
  display: flex;
  justify-content: center;
  align-items: center;
  border-radius: 50%;
  width: 2.75rem;
  height: 2.75rem;
  background: transparent;
  cursor: pointer;
  border: 1.5px solid ${paletteOptions.dream.borderColor};
  color: ${paletteOptions.primary.main};
  font-size: 0.8rem;
  transition: all 0.2s ease;

  &:hover {
    background: ${paletteOptions.primary.main};
    border-color: ${paletteOptions.primary.main};
    color: white;
  }

  &:focus-visible {
    outline: 2px solid ${paletteOptions.primary.main};
    outline-offset: 2px;
  }
`;

const PaginationDots = styled(Box)`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  justify-content: center;

  /* Override Swiper's default absolute positioning on .swiper-pagination */
  &.swiper-pagination {
    position: static;
    width: auto;
  }

  .swiper-pagination-bullet {
    width: 0.5rem;
    height: 0.5rem;
    border-radius: 50%;
    background: #c4bfb5;
    position: relative;

    &::before {
      content: "";
      position: absolute;
      inset: -0.5rem;
    }
    opacity: 1;
    transition: all 0.2s ease;
    cursor: pointer;
  }

  .swiper-pagination-bullet-active {
    background: ${paletteOptions.primary.main};
  }
`;
